import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Page from "components/Page/Page";
import Card from "components/Card/Card";
import { useActiveOrg } from "context/ActiveOrgContext";
import vgToast from "utils/toast";
import {
  CURRENCY,
  Currency,
  DISPUTE,
  ITEM_KIND_LABEL,
  ITEM_STATUS,
  ITEM_STATUS_LABEL,
  ProviderRequest,
  answerRequest,
  formatMoney,
  readProviderRequests,
  refundRequest,
} from "services/marketplace.service";

/**
 * Requests families sent from the marketplace to this provider. Reply to them directly,
 * then accept with your price or decline here; declining a grave plot request makes the plot
 * available again. Once everyone has answered, the family pays through Funely and you are
 * paid your price less Funely's fee.
 */
const ProviderRequests = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { orgId } = useActiveOrg();
  const [requests, setRequests] = React.useState<ProviderRequest[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // The request being accepted, and the price being typed for it.
  const [pricing, setPricing] = React.useState<number | null>(null);
  const [price, setPrice] = React.useState("");
  const [currency, setCurrency] = React.useState<Currency>(CURRENCY.GBP);

  const load = React.useCallback(async () => {
    if (orgId <= 0) return;
    setLoading(true);
    const token = await getAccessTokenSilently();
    const { data, error: readError } = await readProviderRequests(token, orgId);
    setLoading(false);
    if (readError) setError(readError.message);
    else setRequests(data ?? []);
  }, [orgId, getAccessTokenSilently]);

  React.useEffect(() => {
    load();
  }, [load]);

  const decline = async (r: ProviderRequest) => {
    if (!window.confirm(`Decline this request from ${r.contactName}?`)) return;
    const token = await getAccessTokenSilently();
    const { error: answerError } = await answerRequest(token, orgId, r.item.itemId, { status: ITEM_STATUS.DECLINED });
    if (answerError) {
      vgToast.error(answerError.message);
      return;
    }
    vgToast.success("Declined.");
    await load();
  };

  const refund = async (r: ProviderRequest) => {
    const amount = formatMoney(r.item.monetaryAmount, r.item.monetaryCurrency);
    if (!window.confirm(`Refund ${amount} to ${r.contactName}? Your share comes back out of your Stripe account.`)) return;
    const token = await getAccessTokenSilently();
    const { error: refundError } = await refundRequest(token, orgId, r.item.itemId);
    if (refundError) {
      vgToast.error(refundError.message);
      return;
    }
    vgToast.success(`Refunded ${amount}.`);
    await load();
  };

  const accept = async (r: ProviderRequest) => {
    const amount = Number(price);
    if (!Number.isFinite(amount) || amount <= 0) {
      vgToast.error("Give your price to accept.");
      return;
    }
    const token = await getAccessTokenSilently();
    const { error: answerError } = await answerRequest(token, orgId, r.item.itemId, {
      status: ITEM_STATUS.ACCEPTED,
      monetaryAmount: amount,
      monetaryCurrency: currency,
    });
    if (answerError) {
      vgToast.error(answerError.message);
      return;
    }
    vgToast.success("Accepted. The family can pay once every provider has answered.");
    setPricing(null);
    setPrice("");
    await load();
  };

  if (orgId <= 0) {
    return (
      <Page title="Requests">
        <p className="text-sm text-slate-500">Choose an active provider from the header.</p>
      </Page>
    );
  }

  return (
    <Page title="Requests" subTitle="What families have asked you for through the Funely marketplace.">
      <Card>
        <div className="p-6">
          {loading && <p className="text-sm text-slate-500">Loading…</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}
          {!loading && !error && requests.length === 0 && (
            <p className="text-sm text-slate-500">No requests yet. Families find you once your profile is listed.</p>
          )}
          <ul className="flex flex-col divide-y divide-slate-100">
            {requests.map((r) => (
              <li key={r.item.itemId} className="py-4">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {ITEM_KIND_LABEL[r.item.kind]} · {new Date(r.createdOn).toLocaleDateString()}
                    </p>
                    <p className="text-sm font-medium text-slate-900">{r.item.description}</p>
                    <p className="text-sm text-slate-700">
                      {r.contactName}
                      {r.deceasedName && <span className="text-slate-500">, for {r.deceasedName}</span>}
                    </p>
                    <p className="text-sm text-slate-600">
                      {r.contactEmail && <a className="underline" href={`mailto:${r.contactEmail}`}>{r.contactEmail}</a>}
                      {r.contactEmail && r.contactPhone && " · "}
                      {r.contactPhone && <a className="underline" href={`tel:${r.contactPhone}`}>{r.contactPhone}</a>}
                    </p>
                    {r.message && <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{r.message}</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-3 pt-1">
                    {r.item.status === ITEM_STATUS.REQUESTED && pricing === r.item.itemId ? (
                      <form
                        className="flex items-center gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          accept(r);
                        }}
                      >
                        <input
                          className="w-28 rounded-md border border-slate-300 px-2 py-1 text-sm"
                          aria-label="Your price"
                          inputMode="decimal"
                          placeholder="Your price"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          autoFocus
                        />
                        <select
                          className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                          aria-label="Currency"
                          value={currency}
                          onChange={(e) => setCurrency(Number(e.target.value) as Currency)}
                        >
                          {Object.entries(CURRENCY).map(([code, value]) => (
                            <option key={code} value={value}>
                              {code}
                            </option>
                          ))}
                        </select>
                        <button type="submit" className="text-sm font-medium text-slate-800 hover:underline">
                          Accept
                        </button>
                        <button type="button" className="text-sm text-slate-500 hover:underline" onClick={() => setPricing(null)}>
                          Cancel
                        </button>
                      </form>
                    ) : r.item.status === ITEM_STATUS.REQUESTED ? (
                      <>
                        <button type="button" className="text-sm font-medium text-slate-800 hover:underline" onClick={() => setPricing(r.item.itemId)}>
                          Accept
                        </button>
                        <button type="button" className="text-sm text-red-600 hover:underline" onClick={() => decline(r)}>
                          Decline
                        </button>
                      </>
                    ) : (
                      <span className="flex items-center gap-3 text-sm text-slate-600">
                        <span>
                          {ITEM_STATUS_LABEL[r.item.status]}
                          {r.item.status === ITEM_STATUS.ACCEPTED && r.item.monetaryAmount > 0 && (
                            <> · {formatMoney(r.item.monetaryAmount, r.item.monetaryCurrency)}</>
                          )}
                          {r.item.refunded ? <> · refunded</> : r.item.transferred && <> · paid to you</>}
                          {(r.disputeState === DISPUTE.OPEN || r.disputeState === DISPUTE.LOST) && (
                            <> · disputed by the family</>
                          )}
                        </span>
                        {r.item.status === ITEM_STATUS.ACCEPTED && r.paidOn && !r.item.refunded &&
                          r.disputeState !== DISPUTE.OPEN && r.disputeState !== DISPUTE.LOST && (
                            <button type="button" className="text-sm text-red-600 hover:underline" onClick={() => refund(r)}>
                              Refund
                            </button>
                          )}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </Page>
  );
};

export default ProviderRequests;
